import { DraggableAttributes, useDraggable, useDroppable } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { CSSProperties, ReactNode } from "react";

export interface DragHandleProps {
  listeners?: SyntheticListenerMap;
  attributes: DraggableAttributes;
}

interface Props {
  id: string;
  data?: any;
  disabled?: boolean;
  wrapperStyle?: CSSProperties;
  childComponent: (
    props1?: { isDragging: boolean; isOver: boolean; isOverSame: boolean },
    props2?: DragHandleProps
  ) => ReactNode;
  useDragHandle?: boolean;
  disableTransform?: boolean;
  dragOnly?: boolean;
  styles?: CSSProperties;
}

/**
 * Draggable - Basic draggable component
 * @param {string} id - A unique identifier for the Draggable. Must be unique from all other draggables in the DndContext.
 * @param {object} data - Data you want associated with the Draggable/Droppable for dnd event handlers
 * @param {function} childComponent - The component you want to render inside of the Draggable
 * @param {boolean} useDragHandle - Whether or not you are going to use a DragHandle in your childComponent
 * @param {boolean} disableTransform - Whether or not to disable Draggable movement (May be used with DragOverlay)
 * @param {boolean} dragOnly - If true, the Droppable associated with this Draggable will be disabled.
 */
const Draggable = ({
  id,
  data,
  disabled = false,
  wrapperStyle = {},
  childComponent,
  useDragHandle = false,
  disableTransform = false,
  dragOnly = false,
  styles = {},
}: Props) => {
  // For whatever you want to be the drag handle, spread listeners and attributes onto it.
  const { setNodeRef, listeners, attributes, transform, isDragging, active } =
    useDraggable({ id, data, disabled });
  const {
    setNodeRef: setDroppableRef,
    isOver,
    over,
  } = useDroppable({
    id,
    disabled: disabled || !!dragOnly,
    data,
  });

  const isOverSame = active?.id === over?.id;

  const style: CSSProperties = {
    transform:
      !disableTransform && transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
    zIndex: isDragging ? 999 : undefined,
  };

  // When useDragHandle is enabled, we pass the listeners and attributes to the
  // child component so the child component can attach them to their own drag handle.
  if (useDragHandle) {
    const DraggablePart = (
      <div ref={setNodeRef} style={{ ...style, ...styles }}>
        {childComponent(
          { isDragging, isOver, isOverSame },
          { listeners, attributes }
        )}
      </div>
    );

    if (dragOnly) return DraggablePart;

    return (
      <div
        ref={setDroppableRef}
        style={{ display: "flex", justifyContent: "center", ...wrapperStyle }}
      >
        {DraggablePart}
      </div>
    );
  }

  const DraggablePart = (
    <div
      ref={setNodeRef}
      style={{ ...style, ...styles }}
      {...listeners}
      {...attributes}
    >
      {childComponent({ isDragging, isOver, isOverSame })}
    </div>
  );

  if (dragOnly) return DraggablePart;

  return (
    <div ref={setDroppableRef} style={wrapperStyle}>
      {DraggablePart}
    </div>
  );
};

export default Draggable;
